/**
 * Transaction Filters Component
 * Keep the layout and interactions aligned with the Mission page: Search + date on one row, Status on its own row
 */

import React from 'react';
import { Box, Typography, Chip, IconButton } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Close as CloseIcon } from '@mui/icons-material';
import type { PaymentStatus } from '../../../types/finance';
import { SearchBox, DatePicker } from '../../../components/ui';

const transactionStatusOptions: { value: PaymentStatus; label: string }[] = [
  { value: 'Authorized', label: 'Authorized' },
  { value: 'SettlementSubmitted', label: 'Settlement Submitted' },
  { value: 'Settled', label: 'Settled' },
  { value: 'Failed', label: 'Failed' },
  { value: 'Refunded', label: 'Refunded' },
  { value: 'Voided', label: 'Voided' },
  { value: 'Expired', label: 'Expired' },
];

interface DateRange {
  start: string;
  end: string;
}

interface TransactionFiltersProps {
  searchValue: string;
  onSearchChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  dateRange: DateRange | null;
  onDateRangeChange: (field: 'start' | 'end', value: string) => void;
  onClearDateRange: () => void;
  selectedStatuses: Set<PaymentStatus>;
  onStatusToggle: (status: PaymentStatus) => void;
}

export const TransactionFilters: React.FC<TransactionFiltersProps> = ({
  searchValue,
  onSearchChange,
  dateRange,
  onDateRangeChange,
  onClearDateRange,
  selectedStatuses,
  onStatusToggle,
}) => {
  const theme = useTheme();

  return (
    <>
      {/* Search + Date Range (matches the first row on the Mission page) */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          marginBottom: theme.spacing(3),
          flexWrap: 'wrap',
          gap: theme.spacing(2),
        }}
      >
        <SearchBox
          placeholder="Search by Mission Code, Mission Title..."
          value={searchValue}
          onChange={onSearchChange}
          containerSx={{ width: { xs: '100%', md: 400 } }}
        />
        <Box sx={{ display: 'flex', alignItems: 'center', gap: theme.spacing(1) }}>
          <DatePicker
            value={dateRange?.start ?? ''}
            onChange={(e) => onDateRangeChange('start', e.target.value)}
            sx={{ width: 150 }}
          />
          <Typography sx={{ color: theme.palette.text.secondary }}>~</Typography>
          <DatePicker
            value={dateRange?.end ?? ''}
            onChange={(e) => onDateRangeChange('end', e.target.value)}
            sx={{ width: 150 }}
          />
          {dateRange && (
            <IconButton size="small" onClick={onClearDateRange} sx={{ color: theme.palette.text.secondary }}>
              <CloseIcon fontSize="small" />
            </IconButton>
          )}
        </Box>
      </Box>

      {/* Status on its own row (matches the Mission page's "Filter by Status" section) */}
      <Box sx={{ marginBottom: theme.spacing(3) }}>
        <Typography
          variant="subtitle2"
          sx={{
            fontWeight: theme.typography.subtitle2.fontWeight,
            marginBottom: theme.spacing(1.5),
            color: theme.palette.text.primary,
            fontSize: theme.typography.subtitle2.fontSize,
          }}
        >
          Filter by Status:
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: theme.spacing(1) }}>
          {transactionStatusOptions.map((option) => {
            const isSelected = selectedStatuses.has(option.value);
            return (
              <Chip
                key={option.value}
                label={option.label}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onStatusToggle(option.value);
                }}
                onDelete={isSelected ? (e) => {
                  e.stopPropagation();
                  onStatusToggle(option.value);
                } : undefined}
                clickable
                sx={{
                  backgroundColor: isSelected ? theme.palette.primary.main : theme.palette.background.paper,
                  color: isSelected ? theme.palette.primary.contrastText : theme.palette.text.primary,
                  fontWeight: isSelected ? 500 : 400,
                  borderRadius: theme.shapes.borderRadius.lg,
                  border: `1px solid ${isSelected ? theme.palette.primary.main : (theme.customColors?.tableBorder ?? theme.palette.divider)}`,
                  cursor: isSelected ? 'default' : 'pointer',
                  '&:hover': {
                    backgroundColor: isSelected ? theme.palette.primary.dark : (theme.customColors?.tableRowHover ?? theme.palette.action.hover),
                  },
                  '& .MuiChip-deleteIcon': {
                    color: isSelected ? theme.palette.primary.contrastText : theme.palette.text.secondary,
                    '&:hover': { color: isSelected ? theme.palette.primary.contrastText : theme.palette.error.main },
                  },
                }}
              />
            );
          })}
        </Box>
      </Box>
    </>
  );
};
